// WS2 SLICE 1 — THE ONE DORMANCY PREDICATE for sites, farms and bands.
//
// Doc 656 found ~10 rival expressions of "is this thing dormant" across lists and pickers, and doc
// 657/658 found where that drift actually bit: the Farms page called a decommissioned farm
// "Inactive" because its local ternary tested is_inactive first and never tested is_decommissioned
// at all. This module exists so that question has exactly one answer.
//
// TWO THINGS, deliberately separate:
//   • isDormant()      — the BOOLEAN. Is this entity dormant at all?
//   • dormancyReason() — the PRECEDENCE. Which dormancy is it, when several apply?
// 658 proved the boolean alone is not enough: two surfaces can agree a farm is dormant and still
// print different words. Precedence is part of the contract, not a rendering detail.
//
// PRECEDENCE RULE: terminal states outrank reversible ones.
//   farm: decommissioned > site-archived > inactive        (decommissioned is terminal)
//   band: cancelled|released > parked                       (686: BOTH are terminal; parked is a flag)
// This matches Settings>Farms `statusOf()` (FarmsSettings.jsx:11), the surface that was already right.

export const KIND = { SITE: 'site', FARM: 'farm', BAND: 'band', STORAGE: 'storage' };

// ── atomic predicates ─────────────────────────────────────────────────────────
// bandStatusFilter.js imports these two so "parked" and "released" have ONE definition each and
// cannot drift from the pill bar's idea of them.
export const isBandParked   = (b) => !!(b && b.disabled);
export const isBandReleased  = (b) => !!(b && b.band_status === 'released');
// 686: cancelled is the OTHER terminal band status. It was never in the dormancy set, so every
// surface that wanted it excluded hand-rolled an extra `band_status !== 'cancelled'` term
// (ui.jsx:179, ActivitiesPage) — the drift this module exists to end.
export const isBandCancelled = (b) => !!(b && b.band_status === 'cancelled');
export const isSiteArchived = (s) => !!(s && s.is_archived);
// A farm inherits dormancy from an archived parent site. /api/farms joins `locations`; /api/seat-view
// plumbs a flat `site_is_archived`. farmActionState.js already resolves both shapes — mirrored here.
export const isFarmSiteArchived = (f) => !!(f && ((f.locations && f.locations.is_archived) || f.site_is_archived));
// 684b: a storage inherits the same way — GET /api/storages does `select('*, locations(*)')`, and the
// server's own write guard already resolves storage -> site (deactivatedEntityError, "Build 93 R1").
// Same two shapes as farms, so the same reader.
export const isStorageSiteArchived = (s) => !!(s && ((s.locations && s.locations.is_archived) || s.site_is_archived));

// ── the reason, in precedence order ───────────────────────────────────────────
export function dormancyReason(entity, kind) {
  if (!entity) return null;
  if (kind === KIND.SITE) return isSiteArchived(entity) ? 'archived' : null;
  if (kind === KIND.FARM) {
    if (entity.is_decommissioned) return 'decommissioned';   // terminal — outranks everything
    if (isFarmSiteArchived(entity)) return 'site_archived';
    if (entity.is_inactive) return 'inactive';
    return null;
  }
  if (kind === KIND.BAND) {
    // Both terminal statuses outrank the parked FLAG. band_status is a single enum, so released and
    // cancelled are mutually exclusive and their relative order never arbitrates anything.
    if (isBandCancelled(entity)) return 'cancelled';
    if (isBandReleased(entity)) return 'released';
    if (isBandParked(entity)) return 'parked';
    return null;
  }
  // 684b — STORAGE is FARM-shaped, not band-shaped (684-pre). Bands carry TWO ORTHOGONAL axes
  // (`disabled` is independent of band_status, so active+parked is a real cell). Storage is an
  // ORDERED LADDER: decommission writes `{ is_decommissioned: true, is_inactive: true }`, so
  // decommissioned ⇒ inactive and there is no "decommissioned but active" cell. That is exactly the
  // farm shape (same two column names), so this is the farm branch verbatim — and it needs NO new
  // label: decommissioned / site_archived / inactive already exist in DORMANCY_LABEL.
  if (kind === KIND.STORAGE) {
    if (entity.is_decommissioned) return 'decommissioned';   // terminal — outranks everything
    if (isStorageSiteArchived(entity)) return 'site_archived';
    if (entity.is_inactive) return 'inactive';
    return null;
  }
  return null;
}

export const DORMANCY_LABEL = {
  archived: 'Archived', decommissioned: 'Decommissioned', site_archived: 'Site archived',
  inactive: 'Inactive', released: 'Released', parked: 'Parked', cancelled: 'Cancelled',
};
export const dormancyLabel = (entity, kind) => DORMANCY_LABEL[dormancyReason(entity, kind)] || null;

// ── the boolean ───────────────────────────────────────────────────────────────
export const isDormant = (entity, kind) => dormancyReason(entity, kind) != null;

// ── visibility ────────────────────────────────────────────────────────────────
// `alwaysShowId` keeps a row visible even when dormant and hidden — the currently-SELECTED or
// currently-BOUND entity. Two pickers already hand-roll this (BandsSettings.jsx:424,
// ExpenseReallocationPage.jsx:450) precisely so editing a record bound to a now-dormant farm does
// not blank the field. Centralised here so slice 2 does not re-invent it per site.
export function isVisible(entity, kind, showDormant, alwaysShowId = null) {
  if (showDormant) return true;
  if (!isDormant(entity, kind)) return true;
  return alwaysShowId != null && entity && entity.id === alwaysShowId;
}
export const filterDormant = (list, kind, showDormant, alwaysShowId = null) =>
  (list || []).filter(e => isVisible(e, kind, showDormant, alwaysShowId));

// ── OPTION B rendering: off = hidden, on = shown but DIMMED ───────────────────
// The dim is what "shown" looks like; it is never the hiding mechanism. Surfaces that previously
// dimmed unconditionally (FarmsViewA:159, FarmsSettings:182) were expressing dormancy with no way
// to filter — this makes the dim the *visible* half of an actual toggle.
export const DORMANT_OPACITY = 0.55;
export const dormancyRowStyle = (entity, kind) =>
  (isDormant(entity, kind) ? { opacity: DORMANT_OPACITY } : null);
